import React, { useDeferredValue, useMemo } from 'react';
import { Tree, type NodeRendererProps } from 'react-arborist';
import { Box, Text, Tooltip } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import type { TaskSnapshot } from '../../../../stores/types';
import { buildPredecessorValidityMap } from '../../../../gantt/predecessorValidation';
import {
  buildPredecessorTree,
  filterPredecessorTree,
  type PredecessorTreeNode,
} from './predecessorPickerUtils';

export interface PredecessorTaskTreeProps {
  tasks: TaskSnapshot[];
  /** When true, tasks are already outline-sorted (e.g. store.getTasks()). */
  tasksAlreadySorted?: boolean;
  targetTaskId: string;
  searchTerm: string;
  selectedSourceId: string | null;
  selectedSourceIds: Set<string>;
  /** Existing dependency links — used to dim cycle-forming predecessors. */
  links?: Array<{ source: string | number; target: string | number }>;
  width: number;
  height: number;
  onSelectNode: (node: PredecessorTreeNode, selectable: boolean, reason?: string) => void;
}

function TreeNode({
  node,
  style,
  dragHandle,
  selectable,
  reason,
  isPicked,
  isPending,
  onSelectNode,
}: NodeRendererProps<PredecessorTreeNode> & {
  selectable: boolean;
  reason?: string;
  isPicked: boolean;
  isPending: boolean;
  onSelectNode: PredecessorTaskTreeProps['onSelectNode'];
}) {
  // Keep arborist's absolute `style` on the outermost element so Tooltip (or any
  // wrapper) cannot break row hit-testing / virtualization.
  const row = (
    <div
      ref={dragHandle}
      className={
        selectable
          ? 'pred-picker-tree-row'
          : 'pred-picker-tree-row pred-picker-tree-row--disabled'
      }
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        paddingRight: 8,
        width: '100%',
        height: '100%',
        cursor: selectable ? 'pointer' : 'default',
        background: isPending
          ? 'color-mix(in srgb, var(--mantine-color-brand-6) 12%, transparent)'
          : isPicked
            ? 'color-mix(in srgb, var(--mantine-color-teal-6) 10%, transparent)'
            : undefined,
        borderRadius: 6,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelectNode(node.data, selectable, reason);
      }}
    >
      {node.isInternal ? (
        <button
          type="button"
          className="pred-picker-tree-expand"
          aria-label={node.isOpen ? 'Collapse' : 'Expand'}
          title={selectable ? undefined : 'Expand to browse child tasks'}
          onClick={(e) => {
            e.stopPropagation();
            node.toggle();
          }}
          style={{
            width: 20,
            height: 20,
            flexShrink: 0,
            border: 'none',
            background: 'transparent',
            padding: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--mantine-color-text)',
          }}
        >
          {node.isOpen ? <IconChevronDown size={14} stroke={2} /> : <IconChevronRight size={14} stroke={2} />}
        </button>
      ) : (
        <span style={{ width: 20, height: 20, flexShrink: 0 }} aria-hidden />
      )}
      <Text
        size="xs"
        fw={isPicked || isPending ? 600 : selectable ? 500 : 400}
        truncate
        className={selectable ? undefined : 'pred-picker-tree-label--disabled'}
        style={{
          flex: 1,
          minWidth: 0,
          lineHeight: 1.3,
          cursor: selectable ? 'pointer' : 'not-allowed',
        }}
      >
        {node.data.label}
      </Text>
      {isPicked ? (
        <Text size="xs" c="teal" fw={700} style={{ flexShrink: 0 }}>
          Added
        </Text>
      ) : null}
    </div>
  );

  return (
    <div style={style}>
      {!selectable && reason ? (
        <Tooltip
          label={reason}
          multiline
          w={260}
          withArrow
          position="right"
          withinPortal
          zIndex={1300}
        >
          {row}
        </Tooltip>
      ) : (
        row
      )}
    </div>
  );
}

export function PredecessorTaskTree({
  tasks,
  tasksAlreadySorted = false,
  targetTaskId,
  searchTerm,
  selectedSourceId,
  selectedSourceIds,
  links = [],
  width,
  height,
  onSelectNode,
}: PredecessorTaskTreeProps) {
  const deferredSearch = useDeferredValue(searchTerm);
  const hasSearch = deferredSearch.trim().length > 0;

  const roots = useMemo(() => {
    const withoutTarget = tasks.filter((t) => String(t.id) !== String(targetTaskId));
    return buildPredecessorTree(withoutTarget, { alreadySorted: tasksAlreadySorted });
  }, [tasks, targetTaskId, tasksAlreadySorted]);

  const data = useMemo(
    () => filterPredecessorTree(roots, deferredSearch),
    [roots, deferredSearch],
  );

  // Single O(n + L) pass — never call validatePredecessorLink per row at 16k scale.
  const validity = useMemo(
    () => buildPredecessorValidityMap(tasks, targetTaskId, links),
    [tasks, targetTaskId, links],
  );

  if (data.length === 0) {
    return (
      <Box p="md">
        <Text size="sm" c="dimmed">
          {hasSearch ? 'No tasks match your search.' : 'No tasks in this schedule.'}
        </Text>
      </Box>
    );
  }

  return (
    <Tree<PredecessorTreeNode>
      data={data}
      width={width}
      height={height}
      rowHeight={32}
      indent={16}
      // Collapsed by default keeps virtualization cheap at 16k; expand search hits only.
      openByDefault={hasSearch}
      disableDrag
      disableDrop
      disableEdit
      idAccessor={(n) => n.id}
      childrenAccessor={(n) => n.children}
    >
      {(props) => {
        const v = validity.get(props.node.id) ?? { ok: false, reason: 'Unavailable' };
        return (
          <TreeNode
            {...props}
            selectable={v.ok}
            reason={!v.ok ? v.reason : undefined}
            isPicked={selectedSourceIds.has(props.node.id)}
            isPending={selectedSourceId === props.node.id}
            onSelectNode={onSelectNode}
          />
        );
      }}
    </Tree>
  );
}
